import React, { useState } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faBath, faRuler, faMapMarkerAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

interface Property {
  id?: string | number;
  mlsNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  price?: number;
  bedrooms?: number;
  bathrooms?: number;
  squareFeet?: number;
  images?: string[];
  latitude?: number;
  longitude?: number;
  listingBrokerage?: string;
  listingAgent?: string;
  description?: string;
}

interface InlinePropertyCardsProps {
  properties: Property[];
  onViewAll?: () => void;
  maxDisplay?: number;
  onPropertyClick?: (property: Property) => void;
}

const InlinePropertyCards: React.FC<InlinePropertyCardsProps> = ({ 
  properties, 
  onViewAll,
  maxDisplay = 3,
  onPropertyClick 
}) => {
  const navigate = useNavigate();
  const displayProperties = properties.slice(0, maxDisplay);
  const [imageIndexes, setImageIndexes] = useState<{ [key: string]: number }>({});
  
  // Debug logging
  console.log('🎴 [InlinePropertyCards] Properties received:', properties);
  if (properties.length > 0) {
    console.log('🎴 [InlinePropertyCards] First property:', properties[0]);
    console.log('🎴 [InlinePropertyCards] First property description:', properties[0].description);
    console.log('🎴 [InlinePropertyCards] First image URL:', properties[0].images?.[0]);
  }

  const handlePropertyClick = (property: Property, event: React.MouseEvent) => {
    // Don't navigate if clicking on photo navigation buttons
    const target = event.target as HTMLElement;
    if (target.closest('.photo-nav-button')) {
      return;
    }
    
    // Use callback if provided, otherwise navigate
    if (onPropertyClick) {
      onPropertyClick(property);
    } else {
      navigate(`/property/${property.mlsNumber}`, { 
        state: { property } 
      });
    }
  };

  const formatPrice = (price?: number): string => {
    if (!price) return 'Price not available';
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(2)}M`;
    }
    return `$${price.toLocaleString()}`;
  };

  const getCurrentImageIndex = (propertyId: string | number): number => {
    return imageIndexes[String(propertyId)] || 0;
  };

  const handlePrevImage = (propertyId: string | number, totalImages: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const currentIndex = getCurrentImageIndex(propertyId);
    const newIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
    setImageIndexes(prev => ({ ...prev, [String(propertyId)]: newIndex }));
  };

  const handleNextImage = (propertyId: string | number, totalImages: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const currentIndex = getCurrentImageIndex(propertyId);
    const newIndex = currentIndex === totalImages - 1 ? 0 : currentIndex + 1;
    setImageIndexes(prev => ({ ...prev, [String(propertyId)]: newIndex }));
  };

  return (
    <div className="inline-property-cards my-3">
      <Row className="g-3">
        {displayProperties.map((property) => {
          const propertyId = property.id || property.mlsNumber || Math.random().toString();
          const currentImageIndex = getCurrentImageIndex(propertyId);
          const hasMultipleImages = property.images && property.images.length > 1;
          
          return (
            <Col key={propertyId} xs={12} md={4}>
              <Card 
                className="property-card-inline h-100 shadow-sm hover-lift"
                onClick={(e) => handlePropertyClick(property, e)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ height: '160px', overflow: 'hidden', position: 'relative' }}>
                  <Card.Img
                    variant="top"
                    src={property.images?.[currentImageIndex] || 'https://via.placeholder.com/400x300/e9ecef/6c757d?text=No+Image'}
                    alt={property.address}
                    style={{ 
                      height: '100%', 
                      width: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300/e9ecef/6c757d?text=No+Image';
                    }}
                  />
                  
                  {/* Photo Navigation */}
                  {hasMultipleImages && (
                    <>
                      <button
                        className="photo-nav-button position-absolute start-0 top-50 translate-middle-y btn btn-sm btn-dark ms-2"
                        style={{ opacity: 0.8 }}
                        onClick={(e) => handlePrevImage(propertyId, property.images!.length, e)}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <button
                        className="photo-nav-button position-absolute end-0 top-50 translate-middle-y btn btn-sm btn-dark me-2"
                        style={{ opacity: 0.8 }}
                        onClick={(e) => handleNextImage(propertyId, property.images!.length, e)}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                      
                      {/* Photo Indicators */}
                      <div className="position-absolute bottom-0 start-50 translate-middle-x mb-2">
                        <div className="d-flex gap-1">
                          {property.images?.map((_, index) => (
                            <div
                              key={index}
                              className="bg-white rounded-circle"
                              style={{
                                width: '6px',
                                height: '6px',
                                opacity: index === currentImageIndex ? 1 : 0.5
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                <Card.Body className="p-3">
                  <h5 className="text-primary mb-1">{formatPrice(property.price)}</h5>
                  
                  <p className="small text-muted mb-2">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                    {property.city}, {property.state} {property.zipCode}
                  </p>
                  
                  <div className="d-flex gap-3 small">
                    <span>
                      <FontAwesomeIcon icon={faBed} className="text-muted me-1" />
                      {property.bedrooms}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faBath} className="text-muted me-1" />
                      {property.bathrooms}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faRuler} className="text-muted me-1" />
                      {property.squareFeet?.toLocaleString() || 'N/A'}
                    </span>
                  </div>

                  {/* MLS Compliance - Courtesy of Listing Brokerage */}
                  {property.listingBrokerage && (
                    <div className="mt-2 pt-2 border-top">
                      <small className="text-muted d-block">
                        Courtesy of {property.listingBrokerage}
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
      
      {properties.length > maxDisplay && onViewAll && (
        <div className="text-center mt-3">
          <Button 
            variant="primary" 
            onClick={onViewAll}
          >
            View All {properties.length} Properties →
          </Button>
        </div>
      )}
    </div>
  );
};

export default InlinePropertyCards;